import { useCallback, useEffect, useState } from "react";
import { Show, SignInButton, useAuth } from "@clerk/react";
import { BookOpen, ChevronRight, Instagram, Music2 } from "lucide-react";
import { apiFetch } from "@/lib/reels";
import { PrimaryButton, Segment } from "@/components/reels/controls";
import { CreatorLibrary } from "@/components/reels/CreatorLibrary";
import { CombinedLibrary } from "@/components/reels/CombinedLibrary";

const PLATFORMS = [["tiktok", "TikTok"], ["instagram", "Instagram"]];
const STATUS = { importing: "Importing videos…", categorizing: "Sorting into categories…", done: "Ready", error: "Needs attention" };

export const PlatformIcon = ({ platform, size = 16 }) =>
  platform === "instagram" ? <Instagram size={size} /> : <Music2 size={size} />;

const AddCreator = ({ onAdded }) => {
  const { getToken } = useAuth();
  const [platform, setPlatform] = useState("tiktok");
  const [username, setUsername] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const c = await (await apiFetch("/creators", { method: "POST", token: await getToken(), body: JSON.stringify({ platform, username }) })).json();
      setUsername("");
      onAdded(c);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rs-material flex flex-col gap-3.5 p-4 rs-fade" data-testid="creator-add">
      <div>
        <p className="text-[17px] font-extrabold">Import a chef's whole profile</p>
        <p className="rs-secondary text-[14px]">Every video gets sorted into exactly one recipe category.</p>
      </div>
      <Segment options={PLATFORMS} value={platform} onChange={setPlatform} testId="creator-platform" />
      <div className="rs-input flex items-center gap-1">
        <span className="rs-secondary text-[17px]">@</span>
        <input className="rs-field min-w-0 flex-1" placeholder={platform === "tiktok" ? "tiktok username" : "instagram username"}
          value={username} onChange={(e) => setUsername(e.target.value)} onKeyDown={(e) => e.key === "Enter" && username.trim() && !busy && submit()}
          autoCapitalize="none" autoCorrect="off" spellCheck={false} data-testid="creator-username-input" />
      </div>
      {platform === "instagram" && <p className="rs-secondary text-[12px]">Instagram imports use the Instagram account you linked in the Saved tab.</p>}
      <PrimaryButton testId="creator-import-btn" busy={busy} disabled={busy || !username.trim()} onClick={submit} label={busy ? "Starting…" : "Import Profile"} />
      {error && <p className="text-[13px] text-[#c62d22]" data-testid="creator-add-error">{error}</p>}
    </div>
  );
};

const CreatorRow = ({ c, onOpen }) => {
  const pct = c.total ? Math.round((c.categorized / c.total) * 100) : 0;
  return (
    <button className="flex w-full items-center gap-3 py-3 text-left" onClick={onOpen} data-testid={`creator-row-${c.username}`}>
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#efe5d3]"><PlatformIcon platform={c.platform} /></span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[16px] font-bold">@{c.username}</span>
        <span className="rs-secondary block text-[12px]">
          {c.total} videos · {STATUS[c.status] || c.status}{c.status !== "done" && c.total ? ` ${pct}%` : ""}
        </span>
      </span>
      {(c.status === "importing" || c.status === "categorizing") && <span className="rs-spinner" />}
      <ChevronRight size={18} className="rs-secondary" />
    </button>
  );
};

const CreatorList = ({ quality }) => {
  const { getToken } = useAuth();
  const [creators, setCreators] = useState(null);
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    const res = await apiFetch("/creators", { token: await getToken() });
    setCreators(await res.json());
  }, [getToken]);

  useEffect(() => { load().catch(() => setCreators([])); }, [load]);
  useEffect(() => {
    if (!creators?.some((c) => c.status === "importing" || c.status === "categorizing")) return;
    const t = setInterval(() => load().catch(() => {}), 4000);
    return () => clearInterval(t);
  }, [creators, load]);

  if (open === "all") return <CombinedLibrary quality={quality} onBack={() => { setOpen(null); load(); }} />;
  if (open) return <CreatorLibrary creatorId={open} quality={quality} onBack={() => { setOpen(null); load(); }} />;
  return (
    <>
      {creators?.length > 0 && (
        <button className="rs-material flex items-center gap-3 p-4 text-left rs-fade" onClick={() => setOpen("all")} data-testid="combined-library-btn">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#d7352b] text-white"><BookOpen size={20} /></span>
          <span className="min-w-0 flex-1">
            <span className="block text-[17px] font-extrabold">All Recipes</span>
            <span className="rs-secondary block text-[13px]">Every chef together, by category · {creators.reduce((n, c) => n + (c.total || 0), 0)} videos</span>
          </span>
          <ChevronRight size={18} className="rs-secondary" />
        </button>
      )}
      <AddCreator onAdded={(c) => { load(); setOpen(c.id); }} />
      {creators === null ? <div className="flex justify-center"><span className="rs-spinner" /></div> : creators.length > 0 && (
        <div className="rs-material px-4 py-1 rs-fade" data-testid="creator-list">
          <ul className="divide-y divide-[#eadfcb]">{creators.map((c) => <li key={c.id}><CreatorRow c={c} onOpen={() => setOpen(c.id)} /></li>)}</ul>
        </div>
      )}
    </>
  );
};

export const CreatorsPanel = ({ quality }) => (
  <>
    <Show when="signed-out">
      <div className="rs-material flex flex-col items-center gap-3 p-6 text-center rs-fade" data-testid="creators-signed-out">
        <p className="text-[17px] font-extrabold">Build a recipe library from any chef</p>
        <p className="rs-secondary text-[15px]">Sign in, enter a creator's TikTok or Instagram username, and we'll pull every video and sort it by cuisine, meal and diet.</p>
        <SignInButton><button className="rs-prominent w-full" data-testid="creators-sign-in-btn">Sign In</button></SignInButton>
      </div>
    </Show>
    <Show when="signed-in"><CreatorList quality={quality} /></Show>
  </>
);
